import {
	Box,
	Button,
	Card,
	List,
	ListItem,
	ListItemText,
	ListSubheader,
	Stack,
	useTheme,
} from '@mui/material';
import React, {Fragment, useEffect, useState} from 'react';
import {useRecoilValueLoadable} from 'recoil';
import {useRecoilQuery} from 'recoil-toolkit';

import {HttpStatusCode} from 'data/protocols/http';
import {
	AppointmentBookingEnum,
	BookingProcessSuccessEnum,
	FinishWithoutCompleteFlowEnum,
} from 'main/analytics/gaTagsEnum';
import {DateWithWeekday, HighlightedText, SentinelLoading} from 'presentation/components';
import AutocompleteMobileOptions from 'presentation/components/Filter/components/AutocompleteMobileOptions';
import {FeaturesEnum, RecoilLoadableStatesEnum} from 'presentation/constants';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatText, getPageHeaderTitle} from 'presentation/utils';

import {useCurrentValueInputTypingStore} from 'presentation/components/Filter/store/useCurrentValueInputTypingStore';
import {useIsAutocompleteFocusedStore} from 'presentation/components/Filter/store/useIsAutocompleteFocusedStore';
import {DisabilityEnum, FlowEnum, PATH_TITLE, ShiftTypeEnum} from '../../constants';
import {useQueryParamsSync} from '../../hooks/useQueryParamsSync';
import {useSearchBar} from '../../hooks/useSearchBar/useSearchBar';
import {bookingMultipleAvailableTimesState} from '../../pages/ProfessionalSelection/atoms';
import {BookingMultipleAvailableTimesResponse} from '../../pages/ProfessionalSelection/types';

import getFormattedHour from '../../utils/getFormattedHour';
import {BookingEmptyState} from '../BookingEmptyState';
import {BookingErrorState} from '../BookingErrorState';
import {BookingLoadingState} from '../BookingLoadingState';
import {BookingNotFoundState} from '../BookingNotFoundState';
import {BookingNotFoundStateVariantEnum} from '../BookingNotFoundState/types';
import {StyledStack} from './styles';
import {NearestDateProfessionalGridProps} from './types';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import getFilteredOptions from 'presentation/modules/booking/shared/utils/getFilteredOptions';
import {textHandle} from 'presentation/utils/textHandle';
import {FavoriteDoctorsLikeButton} from 'presentation/modules/booking/favoriteDoctors/components/FavoriteDoctorsLikeButton';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {PreviousProviderCard} from 'presentation/modules/booking/shared/ui/components/ProfessionalSelection/PreviousProviderCard';
import Tutorial from 'presentation/components/Tutorial';
import {Placement} from 'react-joyride';
import {HandleMessage} from 'presentation/components/HandleMessage';

const {booking} = translations['pt-br'].others.sidebar;
const {loadingError, loadingState, notFoundModal, emptySearchState} =
	translations['pt-br'].privateFlow.bookingFlow.professionalFlow;
const {emptyDescription} = translations['pt-br'].commons;
const {
	tutorial: {step3},
} = translations['pt-br'].privateFlow.myAppointments;

export function NearestDateProfessionalGrid({
	flowType,
	handlePrev,
	dates,
	handleNext,
}: NearestDateProfessionalGridProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const {activeBookingFlowStep} = useQueryParamsSync();
	const isAutocompleteFocusedState = useIsAutocompleteFocusedStore(
		state => state.isAutocompleteFocused,
	);
	const currentValueInputTypingState = useCurrentValueInputTypingStore(
		state => state.currentValueInputTyping,
	);
	const setCurrentValueInputTypingState = useCurrentValueInputTypingStore(
		state => state.setCurrentValueInputTyping,
	);

	const {SEARCH_PARAMS} = useQueryParams();
	const {shift, disability, autocompleteQuery, query} = SEARCH_PARAMS;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;

	const [datesState, setDatesState] = useState<string[]>(dates);
	const [availableTimesState, setAvailableTimesState] = useState<
		BookingMultipleAvailableTimesResponse[]
	>([]);

	const [isFetchingMoreState, setIsFetchingMoreState] = useState(false);

	const {isMobile} = useMediaQueries();
	const {colors} = useTheme();

	const {handleClearShiftAndSearch, getProviderNamesState} = useSearchBar({
		activeStep: activeBookingFlowStep,
		flowType,
	});

	const normalizedQuery = textHandle(query).sanitizeText();
	const queryToFilter = normalizedQuery === 'clear' ? '' : normalizedQuery;

	const availableTimesParams = {
		cityName: bookingData.cityName,
		dates: datesState,
		disability: disability as DisabilityEnum,
		districtName: isOdontoFlow
			? bookingData.odontoUnitDistrictSelected
			: bookingData.districtName,
		flowType,
		unitCode: bookingData.unitCode,
		shift: shift as ShiftTypeEnum,
		specialtyCode: bookingData.specialtyCode,
		ufCode: bookingData.ufCode,
		subSpecialtyCode: bookingData.subSpecialtyCode,
		providerId:
			(bookingData?.isFavoriteDoctorsFlow && bookingData?.professionalCode) ||
			autocompleteQuery,
	};

	const pageTitle = getPageHeaderTitle({
		internalPaths: PATH_TITLE,
		mainPath: booking,
	});

	const availableTimesList = useRecoilValueLoadable(
		bookingMultipleAvailableTimesState(availableTimesParams),
	);

	const {refresh} = useRecoilQuery(bookingMultipleAvailableTimesState(availableTimesParams));

	const loadMoreDates = () => {
		if (isFetchingMoreState) return;
		setIsFetchingMoreState(true);
		setDatesState(prevDates => {
			if (prevDates.length === 0) return dates.slice(0, 3);

			const lastDate = prevDates[prevDates.length - 1];
			const nextStartIndex = dates.findIndex(date => date === lastDate) + 1;

			const nextDates = dates.slice(nextStartIndex, nextStartIndex + 3);

			if (nextDates.length === 0) return prevDates;

			return nextDates;
		});
	};

	useEffect(() => {
		refresh();
	}, []);

	useEffect(() => {
		setDatesState(dates.slice(0, 3));
		setAvailableTimesState([]);
		setIsFetchingMoreState(true);
	}, [autocompleteQuery, shift]);

	useEffect(() => {
		if (availableTimesList?.state === RecoilLoadableStatesEnum.HAS_VALUE) {
			setAvailableTimesState(prev => {
				const uniqueDates = new Set(prev?.map(item => item?.date));
				const newTimes = availableTimesList?.contents?.filter(
					({date}) => !uniqueDates.has(date),
				);

				return [...prev, ...newTimes];
			});
			setIsFetchingMoreState(false);
		}
	}, [availableTimesList]);

	const {isEnabled: isPreviousProviderEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_PREVIOUS_PROVIDER,
	);

	if (availableTimesList.state === RecoilLoadableStatesEnum.HAS_ERROR) {
		const statusCode = availableTimesList?.contents?.statusCode;

		if (statusCode === HttpStatusCode.unprocessable) {
			return (
				<BookingEmptyState
					title={notFoundModal.emptyTitle}
					description={emptyDescription}
					flowType={flowType}
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_HORARIOS_AGENDAMENTO_ENCONTRADO}
					clearFilter={handleClearShiftAndSearch}
				/>
			);
		}
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={pageTitle}
				errorTitle={loadingError}
				handleBack={handlePrev}
				handleRefresh={refresh}
			/>
		);
	}

	const isLoadingState =
		(availableTimesList.state === RecoilLoadableStatesEnum.LOADING || isFetchingMoreState) &&
		availableTimesState?.length <= 0;

	if (isLoadingState) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={pageTitle}
				loadingText={loadingState}
				handleBack={handlePrev}
			/>
		);
	}

	const NOT_FOUND =
		!availableTimesList.contents || (availableTimesState.length === 0 && !isFetchingMoreState);

	if (NOT_FOUND) {
		return (
			<Box mt={isMobile ? 2 : 4}>
				<BookingNotFoundState
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_HORARIOS_AGENDAMENTO_DISPONIVEL}
					flowType={flowType}
					pageTitle={pageTitle}
					handleBack={handlePrev}
					variant={BookingNotFoundStateVariantEnum.PROFESSIONAL}
					showHeader={false}
				/>
			</Box>
		);
	}

	const filteredErrorQueries = availableTimesState.filter(({times}) => times.length > 0);

	const filteredListByProfessional = filteredErrorQueries.map(availableTimes => ({
		...availableTimes,
		times: !autocompleteQuery
			? availableTimes.times.filter(({professionalName}) =>
					textHandle(professionalName).sanitizeText()?.includes(queryToFilter),
				)
			: availableTimes.times,
	}));

	const isHealthOrTelehealth = flowType === FlowEnum.HEALTH || flowType === FlowEnum.TELEHEALTH;
	const shouldSkipPreviousProvider =
		!isPreviousProviderEnabled || Boolean(autocompleteQuery) || !isHealthOrTelehealth;

	const previousProviderData = (() => {
		if (shouldSkipPreviousProvider) return null;

		const dates = filteredListByProfessional
			.flatMap(({date, times}) => {
				const provider = times.find(t => t.isPreviousProvider);
				if (!provider) return [];
				return [{date, availableTimes: provider.availableTimes}];
			})
			.filter(item => item.availableTimes.length > 0);

		if (!dates.length) return null;

		const provider = filteredListByProfessional
			.flatMap(({times}) => times)
			.find(t => t.isPreviousProvider);

		if (!provider) return null;

		return {
			professionalName: provider.professionalName,
			professionalCode: provider.professionalCode,
			recommendations: provider.recommendations,
			isFavorite: provider.isFavorite,
			dates,
		};
	})();

	const timesWithoutPreviousProvider = shouldSkipPreviousProvider
		? filteredListByProfessional
		: filteredListByProfessional.map(({date, times}) => ({
				date,
				times: times.filter(t => !t.isPreviousProvider),
			}));

	const showPreviousProvider = !shouldSkipPreviousProvider && previousProviderData !== null;

	const options =
		getProviderNamesState?.medicos?.map(({cdPrestadorFisico, nmMedico}) => ({
			id: cdPrestadorFisico,
			label: nmMedico,
		})) || [];

	const FILTERED_OPTIONS = getFilteredOptions(options, currentValueInputTypingState);

	const hasNoAvailableTimes = filteredListByProfessional.every(list => list.times.length === 0);
	const hasFilteredOptions = FILTERED_OPTIONS.length > 0;

	const shouldShowBookingEmptyState =
		(hasNoAvailableTimes && !isFetchingMoreState) ||
		(!hasFilteredOptions && currentValueInputTypingState);

	const shouldShowMobileOptions = isAutocompleteFocusedState && hasFilteredOptions && isMobile;

	const shouldShowAvailableTimesList =
		filteredListByProfessional.some(list => list.times.length > 0) &&
		!isAutocompleteFocusedState &&
		!shouldShowBookingEmptyState;

	const isSentinelOnVisibleActive =
		filteredListByProfessional.length > 0 &&
		hasFilteredOptions &&
		availableTimesList.state === RecoilLoadableStatesEnum.HAS_VALUE &&
		!isFetchingMoreState;

	return (
		<Fragment>
			<List
				sx={{
					width: '100%',
					position: 'relative',
					overflow: 'auto',
				}}
				data-tag-id={BookingProcessSuccessEnum.ENCONTROU_HORARIOS_DISPONIVEIS}
				subheader={<li />}>
				{shouldShowBookingEmptyState ? (
					<BookingEmptyState
						title={
							autocompleteQuery
								? emptySearchState.emptyTitleByDate
								: notFoundModal.emptyTitle
						}
						description={
							autocompleteQuery
								? emptySearchState.emptyDescriptionByProvider
								: emptyDescription
						}
						flowType={flowType}
						dataTagId={
							FinishWithoutCompleteFlowEnum.SEM_HORARIOS_AGENDAMENTO_ENCONTRADO
						}
						clearFilter={() => {
							handleClearShiftAndSearch();
							setCurrentValueInputTypingState('');
						}}
					/>
				) : null}

				{shouldShowMobileOptions ? (
					<AutocompleteMobileOptions options={FILTERED_OPTIONS} />
				) : null}

				{showPreviousProvider && previousProviderData ? (
					<PreviousProviderCard
						flowType={flowType}
						professionalCode={previousProviderData.professionalCode}
						professionalName={previousProviderData.professionalName}
						dates={previousProviderData.dates}
						recommendations={previousProviderData.recommendations}
						onSelectHour={handleNext}
						isFavorite={previousProviderData.isFavorite}
					/>
				) : null}

				{shouldShowAvailableTimesList
					? timesWithoutPreviousProvider.map(timesByDate => {
							if (timesByDate.times.length === 0) return null;

							return (
								<li key={timesByDate.date}>
									<ul>
										<ListSubheader
											sx={{
												marginTop: '16px',
												padding: 0,
												bgcolor: 'transparent',
												color: colors.light.text.primary,
											}}>
											<DateWithWeekday date={timesByDate.date} />
										</ListSubheader>
										{timesByDate.times.map((item, index) => (
											<ListItem key={item.professionalCode} disableGutters>
												<Card
													elevation={0}
													sx={{p: 2, width: '100%'}}
													data-tag-id={
														AppointmentBookingEnum.VISUALIZOU_PROFISSIONAL_DISPONIVEL
													}>
													<Stack
														flexDirection="row"
														alignItems="center"
														justifyContent="space-between">
														<ListItemText
															primary={
																autocompleteQuery ||
																hasFilteredOptions ? (
																	formatText(
																		item.professionalName,
																	)
																) : (
																	<HighlightedText
																		highlight={query}
																		text={formatText(
																			item.professionalName,
																		)}
																	/>
																)
															}
															slotProps={{primary: {variant: 'h6'}}}
														/>
														{flowType === FlowEnum.HEALTH ||
														flowType === FlowEnum.TELEHEALTH ? (
															<FavoriteDoctorsLikeButton
																cdPrestador={Number(
																	item.professionalCode,
																)}
																cdEspecialidade={Number(
																	bookingData.specialtyCode,
																)}
																cdSubEspecialidade={Number(
																	bookingData.subSpecialtyCode,
																)}
																selected={item.isFavorite}
																id={
																	index === 0
																		? 'favoriteButtonTutorialProfessionalGrid'
																		: undefined
																}
															/>
														) : null}
													</Stack>
													<StyledStack
														direction="row"
														gap={1}
														overflow="auto"
														mt={2}
														pb={2}>
														{item.availableTimes.map((hour, index) => (
															<Button
																data-test="button-hour"
																onClick={() =>
																	handleNext({
																		hour,
																		profCode:
																			item.professionalCode,
																		profName:
																			item.professionalName,
																		date: timesByDate.date,
																		recommendations:
																			item.recommendations,
																	})
																}
																key={`${hour}-${index}`}
																variant={
																	bookingData.hourAt === hour &&
																	bookingData.professionalCode ===
																		item.professionalCode &&
																	timesByDate.date ===
																		bookingData.dateAt
																		? 'contained'
																		: 'outlined'
																}
																sx={{
																	borderRadius: 0.5,
																	fontSize: '1rem',
																}}
																data-tag-id={
																	AppointmentBookingEnum.SELECIONOU_HORARIO_DISPONIVEL
																}>
																{getFormattedHour(hour, flowType)}
															</Button>
														))}
													</StyledStack>
												</Card>
											</ListItem>
										))}
									</ul>
								</li>
							);
						})
					: null}

				{!isAutocompleteFocusedState && autocompleteQuery ? (
					<SentinelLoading
						minHeight="50px"
						onVisible={() => {
							if (isSentinelOnVisibleActive) {
								loadMoreDates();
							}
						}}
						isLoading={
							availableTimesState?.length > 0 &&
							availableTimesList.state === RecoilLoadableStatesEnum.LOADING
						}
						sx={{marginTop: 4}}
					/>
				) : null}
			</List>
			<Tutorial
				steps={[
					{
						target: '#favoriteButtonTutorialProfessionalGrid',
						content: <HandleMessage message={step3} />,
						placement: 'top' as Placement,
						disableBeacon: true,
					},
				]}
				storageKey="favoriteButtonTutorialProfessionalGrid"
			/>
		</Fragment>
	);
}
